import type { ReActAction } from '@/components/autopilot/autopilotData';
import { getGoogleAdsOperationErrorMessage, translateApiMessage } from '@/lib/utils';
import type { Project } from '@/types/project';
import type { Site } from '@/types/site';
import type { AutopilotModalMode, ReActItemState } from './types';
import { normalizeLegacyPlatformDomain } from '@/lib/platformDomain';

export const PRELAUNCH_AUTH_FLOW_STORAGE_KEY = 'autopilot-prelaunch-auth-flow-active';
export const PRELAUNCH_POST_ADS_GUIDE_STORAGE_KEY = 'autopilot-prelaunch-post-ads-guide-open';
export const AUTOPILOT_CLONE_FLOW_QUERY_KEY = 'autopilotClone';
export const AUTOPILOT_WORKSPACE_TOUR_SEEN_STORAGE_KEY = 'autopilot-workspace-tour-seen';
export const AUTOPILOT_DASHBOARD_WELCOME_SEEN_STORAGE_KEY = 'autopilot-dashboard-welcome-seen';
export const AUTOPILOT_DASHBOARD_ENTRY_PENDING_STORAGE_KEY = 'autopilot-dashboard-entry-pending';
export const AUTOPILOT_POST_ADS_GUIDE_SOURCE_STORAGE_KEY = 'autopilot-post-ads-guide-source';
export const AUTOPILOT_POST_ADS_GUIDE_SEEN_STORAGE_KEY = 'autopilot-post-ads-guide-seen';
export const GOOGLE_ADS_ASSET_PAGE_SIZE = 20;
export const GOOGLE_ADS_CONVERSION_LOOKBACK_DAYS = 30;
export const AUTOPILOT_AI_RECORD_PAGE_SIZE = 20;
export const AUTOPILOT_SITE_PAGE_SIZE = 200;
export const AUTOPILOT_AI_PROJECT_TYPE_ID = 999;
const TRUTHY_QUERY_VALUES = new Set(['1', 'true', 'yes']);

export const ts = () => new Date().toTimeString().slice(0, 8);

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    const typedError = error as Error & { code?: string; action?: string };
    if (typedError.code === 'AGENT_TURN_CONFLICT') {
      return 'Autopilot is still finishing the previous turn. Try again after it completes.';
    }
    if (typedError.code === 'AI_CREDIT_NOT_ENOUGH' && typedError.action === 'RECHARGE_POINTS') {
      return 'AI credits are not enough. Recharge points before continuing.';
    }
    if (typedError.code === 'MISSING_PARAMS') {
      return 'Agent Runtime needs more parameters before it can continue.';
    }
    if (error.message) return translateApiMessage(error.message);
  }
  return fallback;
};

export const getGoogleAdsAdActivationErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return getGoogleAdsOperationErrorMessage(error.message, {
      resourceType: 'ad',
      nextStatus: 'ENABLED',
    });
  }
  return fallback;
};

export const isTruthyAutopilotQueryValue = (value: string | null | undefined) =>
  TRUTHY_QUERY_VALUES.has((value || '').trim().toLowerCase());

export const hasSeenAutopilotWorkspaceTour = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(AUTOPILOT_WORKSPACE_TOUR_SEEN_STORAGE_KEY) === '1';
};

export const markAutopilotWorkspaceTourSeen = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTOPILOT_WORKSPACE_TOUR_SEEN_STORAGE_KEY, '1');
};

export const hasSeenAutopilotDashboardWelcome = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(AUTOPILOT_DASHBOARD_WELCOME_SEEN_STORAGE_KEY) === '1';
};

export const markAutopilotDashboardWelcomeSeen = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUTOPILOT_DASHBOARD_WELCOME_SEEN_STORAGE_KEY, '1');
};

export const markAutopilotDashboardEntryPending = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(AUTOPILOT_DASHBOARD_ENTRY_PENDING_STORAGE_KEY, '1');
};

export const hasAutopilotDashboardEntryPending = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(AUTOPILOT_DASHBOARD_ENTRY_PENDING_STORAGE_KEY) === '1';
};

export const consumeAutopilotDashboardEntryPending = () => {
  if (typeof window === 'undefined') return false;
  const shouldShow =
    window.sessionStorage.getItem(AUTOPILOT_DASHBOARD_ENTRY_PENDING_STORAGE_KEY) === '1';
  window.sessionStorage.removeItem(AUTOPILOT_DASHBOARD_ENTRY_PENDING_STORAGE_KEY);
  return shouldShow;
};

export const setAutopilotPostAdsGuideSource = (source: 'dashboard' | 'clone' | 'prelaunch') => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(AUTOPILOT_POST_ADS_GUIDE_SOURCE_STORAGE_KEY, source);
};

export const getAutopilotPostAdsGuideSource = () => {
  if (typeof window === 'undefined') return null;
  const value = window.sessionStorage.getItem(AUTOPILOT_POST_ADS_GUIDE_SOURCE_STORAGE_KEY);
  return value === 'dashboard' || value === 'clone' || value === 'prelaunch' ? value : null;
};

export const clearAutopilotPostAdsGuideSource = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(AUTOPILOT_POST_ADS_GUIDE_SOURCE_STORAGE_KEY);
};

const getAutopilotPostAdsGuideSeenKey = (siteId: number | null | undefined) =>
  `${AUTOPILOT_POST_ADS_GUIDE_SEEN_STORAGE_KEY}:${siteId ?? 'default'}`;

export const hasSeenAutopilotPostAdsGuide = (siteId: number | null | undefined) => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(getAutopilotPostAdsGuideSeenKey(siteId)) === '1';
};

export const markAutopilotPostAdsGuideSeen = (siteId: number | null | undefined) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(getAutopilotPostAdsGuideSeenKey(siteId), '1');
};

export const clearAutopilotPostAdsGuideSeen = (siteId: number | null | undefined) => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(getAutopilotPostAdsGuideSeenKey(siteId));
};

export const getCloneSourceUrlFromProjectDescription = (description?: string | null) => {
  const trimmed = String(description || '').trim();
  if (!trimmed) return null;

  const match = trimmed.match(/^clone source:\s*(https?:\/\/\S+)$/i);
  return match?.[1]?.trim() || null;
};

export const isCloneGeneratedProject = (
  project: Pick<Project, 'projectDescription'> | null | undefined,
) => Boolean(getCloneSourceUrlFromProjectDescription(project?.projectDescription));

export const toFiniteNumber = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const formatDateForApi = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getLookbackDateRange = (days: number) => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (days - 1));

  return {
    startDate: formatDateForApi(startDate),
    endDate: formatDateForApi(endDate),
  };
};

export const dedupeStringIds = (items: string[]) => [...new Set(items)];

export const emptyReActState = (): ReActItemState => ({
  selectedActionIds: [],
  completedActionIds: [],
  status: 'idle',
  results: [],
});

export const getSelectedActions = (actions: ReActAction[] | undefined, selectedIds: string[]) =>
  selectedIds
    .map((actionId) => actions?.find((action) => action.id === actionId))
    .filter(Boolean) as ReActAction[];

export const getActiveUserProjectId = (
  queryUserProjectId: string | null,
  project: Project | null,
) => {
  const queryId = Number(queryUserProjectId);
  if (Number.isFinite(queryId) && queryId > 0) return queryId;

  const projectId = Number(project?.id);
  return Number.isFinite(projectId) && projectId > 0 ? projectId : null;
};

export const findBestMatchingSite = (
  sites: Site[],
  options: {
    activeSiteId?: number | null;
    domain?: string | null;
    name?: string | null;
    fallbackToFirst?: boolean;
  } = {},
) => {
  const normalizedDomain =
    normalizeLegacyPlatformDomain(options.domain)?.trim().toLowerCase() || '';
  const normalizedName = options.name?.trim().toLowerCase() || '';

  if (options.activeSiteId) {
    const byId = sites.find((site) => site.id === options.activeSiteId);
    if (byId) return byId;
  }

  if (normalizedDomain) {
    const byDomain =
      sites.find(
        (site) =>
          normalizeLegacyPlatformDomain(site.domain)?.trim().toLowerCase() === normalizedDomain,
      ) ||
      sites.find(
        (site) =>
          normalizeLegacyPlatformDomain(site.originalDomain)?.trim().toLowerCase() ===
          normalizedDomain,
      ) ||
      sites.find((site) => site.name?.trim().toLowerCase() === normalizedDomain);
    if (byDomain) return byDomain;
  }

  if (normalizedName) {
    const byName = sites.find((site) => site.name?.trim().toLowerCase() === normalizedName);
    if (byName) return byName;
  }

  return options.fallbackToFirst ? sites[0] || null : null;
};

export const getAutopilotSiteDisplayLabel = (
  site?: Pick<Site, 'domain' | 'originalDomain' | 'siteKey' | 'name' | 'id'> | null,
) => {
  if (!site) return '';

  const rawValue = (
    normalizeLegacyPlatformDomain(site.domain)?.trim() ||
    normalizeLegacyPlatformDomain(site.originalDomain)?.trim() ||
    normalizeLegacyPlatformDomain(site.siteKey)?.trim() ||
    site.siteKey?.trim() ||
    site.name?.trim() ||
    String(site.id)
  ).trim();
  if (!rawValue) return '';

  const firstSegment = rawValue.split('.')[0]?.trim() || rawValue;
  if (/^site-[a-z0-9-]+$/i.test(firstSegment)) {
    return firstSegment;
  }

  return rawValue;
};

export const getAutopilotAuthModalModeStorageKey = (siteId: number) =>
  `autopilot-google-ads-modal-mode:${siteId}`;

export const getAutopilotAuthModalMode = (siteId: number): AutopilotModalMode | null => {
  if (typeof window === 'undefined') return null;
  const value = window.sessionStorage.getItem(getAutopilotAuthModalModeStorageKey(siteId));
  return value === 'ads' || value === 'activate' ? value : null;
};

export const setAutopilotAuthModalMode = (siteId: number, mode: AutopilotModalMode) => {
  if (typeof window === 'undefined') return;
  if (mode !== 'ads' && mode !== 'activate') return;
  window.sessionStorage.setItem(getAutopilotAuthModalModeStorageKey(siteId), mode);
};

export const clearAutopilotAuthModalMode = (siteId: number) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(getAutopilotAuthModalModeStorageKey(siteId));
};

const getRebindRequiredStorageKey = (siteId: number) => `google-ads-rebind-required:${siteId}`;

export const getRebindRequiredFlag = (siteId: number) => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(getRebindRequiredStorageKey(siteId)) === '1';
};

export const setRebindRequiredFlag = (siteId: number, required: boolean) => {
  if (typeof window === 'undefined') return;
  const key = getRebindRequiredStorageKey(siteId);
  if (required) {
    window.localStorage.setItem(key, '1');
    return;
  }
  window.localStorage.removeItem(key);
};
